import { NOOP } from './constants';
import {
  location,
  history,
  addEventListener,
  removeEventListener,
} from './env/browser';
import createDebug from './utils/debug';

const debug = createDebug('router');

// helpers

function isElement(node: Node): node is Element {
  return node.nodeType === Node.ELEMENT_NODE;
}

export function findRouteLink(node: Node | null): Element | undefined {
  if (!node || !isElement(node)) return undefined;
  if (node.tagName.toLowerCase() === 'body') return undefined;

  const hasDataRoute = node.hasAttribute('data-route');
  const href = node.getAttribute('href');

  if (hasDataRoute && href) return node;

  return findRouteLink(node.parentNode);
}

// router

export interface DeprecatedRouter {
  navigate: (url: string) => void;
  redirect: (url: string) => void;
  load: (url: string) => void;
  onChangeLocation: (
    callback: (url: string) => void,
    shouldTriggerForCurrentLocation?: boolean
  ) => void;
  reloadLocation: () => void;
  subscribe: () => void;
  destroy: () => void;
}

export default function createRouter(): DeprecatedRouter {
  let changeLocationCallback: (url: string) => void = NOOP;

  function handleLocationChange(): void {
    changeLocationCallback(location.href);
  }

  function route(
    url: string,
    { replace = false, forceReload = false } = {}
  ): void {
    debug('navigate', url, { replace, forceReload });

    if (forceReload) {
      location.href = url;
      return;
    }

    if (location.href === url) return;

    // 2nd param is a legacy "title" the History API spec requires but every
    // browser ignores; lib.dom.d.ts types it as a required (non-nullable)
    // string, so '' rather than the original `null` — same no-op in practice.
    history[replace ? 'replaceState' : 'pushState'](null, '', url);
    handleLocationChange();
  }

  function navigate(url: string): void {
    route(url, { replace: false });
  }

  function redirect(url: string): void {
    route(url, { replace: true });
  }

  function load(url: string): void {
    route(url, { forceReload: true });
  }

  function onChangeLocation(
    callback: (url: string) => void,
    shouldTriggerForCurrentLocation = false
  ): void {
    changeLocationCallback = callback;
    if (shouldTriggerForCurrentLocation) handleLocationChange();
  }

  function reloadLocation(): void {
    location.reload();
  }

  function subscribe(): void {
    addEventListener('popstate', handleLocationChange);
  }

  function destroy(): void {
    removeEventListener('popstate', handleLocationChange);
  }

  // public interface

  return Object.freeze({
    navigate,
    redirect,
    load,
    reloadLocation,
    onChangeLocation,
    subscribe,
    destroy,
  });
}
